/**
 * Simulateur pas à pas d'un programme Python (piste I1, moteur v29).
 *
 * L'élève avance ligne par ligne (« Ligne suivante », « Reculer », « Jusqu'à la
 * fin ») : la ligne exécutée est surlignée ; le tableau des variables montre
 * chaque valeur, celle qui vient de changer clignote ; un test (`while`, `if`,
 * `elif`) s'affiche en vert quand il est vrai, en rouge quand il est faux ; un
 * appel de fonction ouvre un cadre avec ses variables et la valeur renvoyée ; ce
 * qu'affiche `print` apparaît dans la console.
 *
 * Le programme est exécuté sur l'appareil par l'interpréteur sûr du moteur
 * (@jeraisonne/reasoner), en arithmétique exacte ; `random` donne les mêmes valeurs
 * que Python (même graine), ou les tirages imposés par l'énoncé. Proposé une fois
 * la question répondue : il donnerait sinon la réponse.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, ChevronsRight, RotateCcw, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { parseDraw, simulateProgram, type SimStep } from '@jeraisonne/reasoner';
import type { ProgramView } from './scratch-session';

/** Le déroulé du programme (pur, testable sans DOM). */
export function simulationOf(program: Pick<ProgramView, 'simulate' | 'call' | 'draws'>) {
  const draws = program.draws.map(parseDraw).filter((d) => d !== null);
  return simulateProgram(program.simulate, {
    ...(program.call ? { call: program.call } : {}),
    ...(draws.length ? { draws } : {}),
  });
}

function StatusLine({ step }: { step: SimStep }) {
  if (step.kind === 'cond' && step.cond)
    return (
      <>
        <code className="sim-cond" data-result={step.cond.result ? 'true' : 'false'}>
          {step.cond.text}
        </code>{' '}
        est {step.cond.result ? 'vrai' : 'faux'}
        {step.label.slice(step.label.indexOf(' : '))}
      </>
    );
  return <>{step.label}</>;
}

export function ProgramSimulator({
  program,
  onClose,
}: {
  program: ProgramView;
  onClose: () => void;
}) {
  const sim = useMemo(() => simulationOf(program), [program]);
  const [idx, setIdx] = useState(0);
  const dialogRef = useRef<HTMLElement>(null);
  const currentRef = useRef<HTMLLIElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const last = sim.steps.length - 1;
  const step = sim.steps[Math.min(idx, last)];
  const lines = program.simulate.split('\n');
  const hasConsole = /\bprint\s*\(/.test(program.simulate);

  // clavier : ← →, Début / Fin, Échap ; la page derrière ne défile pas
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const back = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    // en capture, et arrêtées : la feuille de brouillon dessous ne reçoit pas ces touches
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && dialogRef.current) {
        const items = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled])'),
        );
        e.stopImmediatePropagation();
        if (!items.length) return;
        const first = items[0]!;
        const lastItem = items[items.length - 1]!;
        const active = document.activeElement;
        if (e.shiftKey && (active === first || active === dialogRef.current)) {
          e.preventDefault();
          lastItem.focus();
        } else if (!e.shiftKey && active === lastItem) {
          e.preventDefault();
          first.focus();
        }
        return;
      }
      if (e.key === 'Escape') onCloseRef.current();
      else if (e.key === 'ArrowRight') setIdx((i) => Math.min(i + 1, last));
      else if (e.key === 'ArrowLeft') setIdx((i) => Math.max(i - 1, 0));
      else if (e.key === 'Home') setIdx(0);
      else if (e.key === 'End') setIdx(last);
      else return;
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey, true);
      back?.focus?.();
    };
  }, [last]);

  // la ligne en cours reste en vue
  useEffect(() => {
    const el = currentRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [idx]);

  const top = step?.frames[step.frames.length - 1];

  return createPortal(
    <div className="sc-root fixed inset-0 z-[80] flex items-end justify-center md:items-center md:p-4">
      <div className="sc-scrim absolute inset-0" onClick={onClose} aria-hidden />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="program-simulator-title"
        tabIndex={-1}
        data-testid="program-simulator"
        className="sc-sheet native:h-[calc(100dvh-env(safe-area-inset-top)-0.5rem)] relative flex h-[calc(100dvh-0.5rem)] w-full flex-col overflow-hidden rounded-t-3xl outline-none md:h-[min(92dvh,900px)] md:w-[92vw] md:max-w-[1000px] md:rounded-3xl"
      >
        <header className="flex flex-none items-center gap-2 pb-1 pl-4 pr-2 pt-3 md:pl-5">
          <h2
            id="program-simulator-title"
            className="font-display min-w-0 flex-1 truncate text-[17px] font-bold md:text-lg"
          >
            Simulateur pas à pas
          </h2>
          <span className="font-sans text-sm text-[var(--sc-muted)]" data-testid="sim-counter">
            Pas {Math.min(idx, last) + 1} / {sim.steps.length}
          </span>
          <button
            type="button"
            className="sc-icon-btn"
            aria-label="Fermer le simulateur"
            onClick={onClose}
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3 md:px-5 lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:gap-4">
          <section aria-label="Programme" className="sim-panel mb-3 lg:mb-0">
            {program.display && (
              <p className="mb-2 font-sans text-xs text-[var(--sc-muted)]">
                L’algorithme de l’énoncé, écrit en Python :
              </p>
            )}
            <ol className="sim-code" data-testid="sim-code">
              {lines.map((text, i) => {
                const n = i + 1;
                const current = step?.line === n;
                return (
                  <li
                    key={n}
                    ref={current ? currentRef : undefined}
                    data-current={current ? '' : undefined}
                    data-cond={
                      current && step?.kind === 'cond'
                        ? step.cond?.result
                          ? 'true'
                          : 'false'
                        : undefined
                    }
                    aria-current={current ? 'step' : undefined}
                  >
                    <span className="sim-ln" aria-hidden>
                      {n}
                    </span>
                    <code>{text || ' '}</code>
                  </li>
                );
              })}
            </ol>
          </section>

          <div className="flex min-w-0 flex-col gap-3">
            <p
              className="sim-status"
              data-kind={step?.kind}
              aria-live="polite"
              data-testid="sim-status"
            >
              {step && <StatusLine step={step} />}
            </p>

            <section aria-label="Variables" className="flex flex-col gap-2">
              {step?.frames.map((f) => (
                <div
                  key={f.id}
                  className="sim-frame"
                  data-call={f.id ? '' : undefined}
                  data-testid="sim-frame"
                >
                  <p className="sim-frame-name">
                    {f.id ? (
                      <>
                        Appel <code>{f.name}</code>
                      </>
                    ) : (
                      'Variables du programme'
                    )}
                    {f === top && step?.ret !== undefined && (
                      <span className="sim-ret">
                        renvoie <code>{step?.ret}</code>
                      </span>
                    )}
                  </p>
                  {f.vars.length ? (
                    <table className="sim-vars">
                      <tbody>
                        {f.vars.map((v) => (
                          <tr key={v.name}>
                            <th scope="row">
                              <code>{v.name}</code>
                            </th>
                            <td>
                              <code
                                key={v.changed ? `${idx}-${v.name}` : v.name}
                                className={cn(v.changed && 'sim-changed')}
                                data-changed={v.changed ? '' : undefined}
                              >
                                {v.value}
                              </code>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="sim-empty">Aucune variable pour l’instant.</p>
                  )}
                </div>
              ))}
            </section>

            {hasConsole && (
              <section aria-label="Console" className="sim-console" data-testid="sim-console">
                <p className="sim-frame-name">Console (ce qu’affiche print)</p>
                {step?.output.length ? (
                  <pre>{step.output.join('\n')}</pre>
                ) : (
                  <p className="sim-empty">Rien d’affiché pour l’instant.</p>
                )}
              </section>
            )}

            {sim.truncated && idx === last && (
              <p className="sim-empty">
                Le simulateur s’arrête après {sim.steps.length} pas : le programme continue.
              </p>
            )}
          </div>
        </div>

        <footer className="flex flex-none items-center gap-2 border-t border-[var(--sc-line)] px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 md:px-5">
          <button
            type="button"
            className="sim-btn"
            aria-label="Recommencer"
            disabled={idx === 0}
            onClick={() => setIdx(0)}
          >
            <RotateCcw className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            className="sim-btn"
            aria-label="Reculer d’un pas"
            disabled={idx === 0}
            onClick={() => setIdx((i) => Math.max(i - 1, 0))}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            className="sim-btn sim-btn-main flex-1"
            disabled={idx >= last}
            onClick={() => setIdx((i) => Math.min(i + 1, last))}
          >
            Ligne suivante
            <ChevronRight className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            className="sim-btn"
            aria-label="Aller jusqu’à la fin"
            disabled={idx >= last}
            onClick={() => setIdx(last)}
          >
            <ChevronsRight className="h-5 w-5" aria-hidden />
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
